import { useEffect, useState } from "react";
import { CV_EDUCATION_LABELS, CV_EDUCATION_LEVELS, type AnalysisPage, type CvAnalysis, type ExtractionLot } from "@shared/modules/x-extraction";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { listAnalyses, listLots } from "../lib/api";
import { AnalysisTable } from "../components/analysis-table";
import { ProfileModal } from "../components/profile-modal";

const EMPTY = { q: "", niveau: "", experienceMin: "", langue: "", lotId: "", tri: "recent" };
const PAGE_SIZE = 25;

/**
 * Le vivier : tous les CV analysés, tous lots confondus, pour retrouver un
 * profil par compétence, diplôme, expérience ou langue. C'est aussi la base
 * du futur assistant de recherche.
 */
export function VivierPage() {
  const push = useToastStore((s) => s.push);
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AnalysisPage | null>(null);
  const [lots, setLots] = useState<ExtractionLot[]>([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<CvAnalysis | null>(null);

  const load = async (p = page, f = filters) => {
    setBusy(true);
    try {
      setData(await listAnalyses({ ...f, page: p, pageSize: PAGE_SIZE }));
      setPage(p);
    } catch {
      push("danger", "Impossible de charger le vivier");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load(1);
    listLots(true)
      .then(setLots)
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setFilters({ ...filters, [k]: e.target.value });
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="page">
      <PageHeader title="Vivier" subtitle={data ? `${data.total} CV analysé${data.total > 1 ? "s" : ""}` : "Tous les CV analysés"} />

      <form
        className="vivier-filters"
        onSubmit={(e) => {
          e.preventDefault();
          void load(1);
        }}
      >
        <Field label="Recherche" hint="Poste, compétence, outil, diplôme, établissement, nom… (tous les mots)">
          <Input value={filters.q} onChange={set("q")} placeholder="ex. soudage TIG chantier naval" />
        </Field>
        <Field label="Plus haut diplôme">
          <Select value={filters.niveau} onChange={set("niveau")}>
            <option value="">Tous</option>
            {CV_EDUCATION_LEVELS.map((l) => (
              <option key={l} value={l}>
                {CV_EDUCATION_LABELS[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Expérience minimale (années)">
          <Input type="number" min={0} max={60} value={filters.experienceMin} onChange={set("experienceMin")} />
        </Field>
        <Field label="Langue">
          <Input value={filters.langue} onChange={set("langue")} placeholder="ex. anglais" />
        </Field>
        <Field label="Lot">
          <Select value={filters.lotId} onChange={set("lotId")}>
            <option value="">Tous</option>
            {lots.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Trier par">
          <Select value={filters.tri} onChange={set("tri")}>
            <option value="recent">Plus récents</option>
            <option value="experience">Expérience</option>
            <option value="nom">Nom</option>
          </Select>
        </Field>
        <div className="vivier-filters__actions">
          <Button type="submit" variant="primary" disabled={busy} icon={<Icon name="search" size={16} />}>
            Rechercher
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setFilters(EMPTY);
              void load(1, EMPTY);
            }}
          >
            Effacer
          </Button>
        </div>
      </form>

      <AnalysisTable items={data?.items ?? []} onSelect={setSelected} empty={busy ? "Chargement…" : "Aucun CV ne correspond."} />

      {pages > 1 ? (
        <div className="vivier-pagination">
          <Button variant="ghost" size="sm" disabled={busy || page <= 1} onClick={() => void load(page - 1)}>
            Précédent
          </Button>
          <span className="muted">
            Page {page} / {pages}
          </span>
          <Button variant="ghost" size="sm" disabled={busy || page >= pages} onClick={() => void load(page + 1)}>
            Suivant
          </Button>
        </div>
      ) : null}

      <ProfileModal analysis={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
